import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import SignatureCanvas from '../SignatureCanvas';
import { openPdfBlob } from '../../utils/pdfOpener';

export const ouvrirPdf = async (url, nom, mode = 'view') => {
  try {
    const { data } = await axios.get(url, { responseType: 'blob', timeout: 60000 });
    openPdfBlob(data, nom, mode);
  } catch {
    toast.error('Impossible d\'ouvrir le document');
  }
};

const useSignatureProfil = (uid) => {
  const [sig, setSig] = useState(null);
  useEffect(() => {
    axios.get(`/signatures/${uid}`).then(({ data }) => {
      if (data?.signature) return setSig(data.signature);
      return axios.get('/candidats/ma-signature').then(({ data: d }) => d?.signature && setSig(d.signature));
    }).catch(() => axios.get('/candidats/ma-signature').then(({ data: d }) => d?.signature && setSig(d.signature)).catch(() => {}));
  }, [uid]);
  return sig;
};

export const SignerReglementDialog = ({ ouvert, onFermer, r, uid, onSigne }) => {
  const [accepte, setAccepte] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const sigProfil = useSignatureProfil(uid);
  const nom = `${r.prenom} ${r.nom}`.trim();

  const signer = async (signature) => {
    if (!accepte) return toast.error('Cochez la phrase d\'acceptation avant de signer');
    setEnvoi(true);
    try {
      await axios.post('/reglement-interieur/signer', { signature, accepte });
      toast.success('Règlement intérieur signé, merci !');
      onSigne();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signature impossible, réessayez.');
    } finally {
      setEnvoi(false);
    }
    return null;
  };

  return (
    <Dialog open={ouvert} onOpenChange={(v) => !v && onFermer()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="reglement-dialog">
        <DialogHeader>
          <DialogTitle>Règlement intérieur FISL</DialogTitle>
          <DialogDescription>Lisez le règlement, cochez la phrase d'acceptation puis signez.</DialogDescription>
        </DialogHeader>
        <button type="button" onClick={() => ouvrirPdf('/reglement-interieur/pdf', 'Reglement_interieur_FISL.pdf')} data-testid="reglement-lire-pdf"
          className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left text-sm font-semibold text-[#0F172A] transition-colors hover:bg-slate-100">
          <FileText className="h-5 w-5 shrink-0 text-[#0E7C74]" /> 1. Lire le règlement intérieur (PDF, 5 pages)
        </button>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-sm leading-relaxed text-slate-700" data-testid="reglement-acceptation">
          <input type="checkbox" checked={accepte} onChange={(e) => setAccepte(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-emerald-600" data-testid="reglement-case-acceptation" />
          <span>2. Je soussigné(e) <b>{nom}</b>, participant à la formation <b>{r.formation}</b>, organisée par le centre FISL, reconnais avoir pris
            connaissance du présent règlement intérieur et m'engage à en respecter les termes intégralement.</span>
        </label>
        <p className="text-sm font-semibold text-[#0F172A]">3. Ma signature <span className="font-normal text-slate-500">(la mention « Lu et approuvé » et la date sont ajoutées automatiquement)</span></p>
        <SignatureCanvas onSave={signer} loading={envoi} existingSignature={sigProfil} buttonText="Signer le règlement" height={160} />
      </DialogContent>
    </Dialog>
  );
};
